import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKey, EnvelopeSimple, ArrowRight, WarningCircle } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/pricelist", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (params.get("expired") === "1") {
      toast.warning("Your session has expired. Please sign in again.");
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/pricelist", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-canvas" data-testid="login-page">
      {/* Left brand rail */}
      <div className="absolute inset-y-0 left-0 w-[42%] hidden lg:flex flex-col justify-between p-14 brand-rail text-white overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-black/10 blur-3xl" aria-hidden="true" />
        <div className="relative inline-flex items-center gap-3 self-start">
          <div className="bg-white rounded-xl px-4 py-2.5 shadow-lg">
            <img src="/bwz-logo.png" alt="BrandOnWheelz" className="h-12 w-auto object-contain" draggable="false" />
          </div>
        </div>
        <div className="space-y-8 max-w-md">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/85 font-semibold">
            <span className="h-px w-10 bg-white/70" />
            Internal Sales Console
          </div>
          <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95] tracking-tight">
            The pricelist,<br />
            <span className="italic">on wheelz.</span>
          </h1>
          <p className="text-white/85 text-base leading-relaxed font-body">
            One source of truth for every service, every city, every rate card.
            Sales, quote &amp; close — faster than a car in Bengaluru traffic.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[
              { k: "Cities", v: "24+" },
              { k: "Formats", v: "18" },
              { k: "Session", v: "30 min" },
            ].map((s) => (
              <div key={s.k} className="border-l border-white/40 pl-4">
                <div className="font-display font-black text-2xl">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-white/60 uppercase tracking-[0.2em] font-semibold">
          © 2026 BrandOnWheelz · Confidential
        </div>
      </div>

      {/* Login card */}
      <div className="min-h-screen flex items-center justify-center px-6 lg:pl-[46%] lg:pr-16 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><BrandLogo /></div>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3 font-bold">Sign in</div>
            <h2 className="font-display font-bold text-zinc-900 text-3xl">Access the console</h2>
            <p className="text-zinc-500 text-sm mt-2">
              Sales &amp; admin only. Sessions auto-expire after 30 minutes.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">
                Email
              </Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@brandonwheelz.com"
                  data-testid="login-email"
                  className="pl-10 h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-brand focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-bold">
                Password
              </Label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="pl-10 h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-brand focus-visible:ring-offset-0"
                />
              </div>
            </div>

            {error && (
              <div
                className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                <WarningCircle size={16} weight="bold" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 bg-brand hover:bg-brand-hover text-white font-bold rounded-md gap-2 group shadow-sm"
            >
              {loading ? "Signing in..." : "Sign in to Console"}
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
            <div className="uppercase tracking-widest text-zinc-600 mb-2 font-bold">
              Trouble signing in?
            </div>
            <div>
              Contact your BrandOnWheelz administrator to reset your password or request access.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
